import React, { useEffect, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/autoplay';

import { Navigation, Pagination, Autoplay } from 'swiper/modules';

import Layout from '../../components/layout/Layout';
import { functionsService as functions, auth, db } from '../../firebase';
import Spinner from '../../components/common/Spinner';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { FaCheckCircle } from 'react-icons/fa';

const UpgradePage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const getProducts = httpsCallable(functions, 'getProducts');
        const response = await getProducts();

        if (
          response &&
          response.data &&
          response.data.status === 'success' &&
          Array.isArray(response.data.products)
        ) {
          setProducts(response.data.products);
        } else {
          throw new Error('Formato de resposta inesperado');
        }
      } catch (err) {
        setError(
          'Erro ao carregar produtos do Stripe. Por favor, tente novamente mais tarde.'
        );
        console.error('Erro ao carregar produtos do Stripe:', err);
      } finally {
        setLoading(false);
      }
    };

    const fetchUserData = async () => {
      try {
        const user = auth.currentUser;
        if (user) {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            setUserData(userDoc.data());
          } else {
            throw new Error('Usuário não encontrado.');
          }
        }
      } catch (err) {
        setError('Erro ao carregar informações do usuário.');
        console.error('Erro ao carregar informações do usuário:', err);
      }
    };

    fetchProducts();
    fetchUserData();
  }, []);

  const handleCheckout = async (priceId) => {
    if (!userData) {
      alert('Usuário não carregado.');
      return;
    }

    try {
      const createCheckoutSession = httpsCallable(
        functions,
        'createCheckoutSession'
      );
      const response = await createCheckoutSession({
        priceId,
        nome: userData.nome,
        email: userData.email,
        cpf: userData.cpf || 'N/A',
      });

      if (response.data.status === 'success') {
        window.location.href = response.data.url;
      } else {
        console.error(
          'Erro ao redirecionar para o checkout:',
          response.data.message
        );
        alert('Falha ao iniciar o checkout. Por favor, tente novamente.');
      }
    } catch (error) {
      console.error('Erro ao redirecionar para o checkout:', error);
      alert('Falha ao iniciar o checkout. Por favor, tente novamente.');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-screen">
          <Spinner aria-label="Carregando produtos" />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-screen">
          <p
            className="text-error text-lg font-semibold"
            role="alert"
            aria-live="assertive"
          >
            {error}
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <Swiper
          modules={[Navigation, Pagination, Autoplay]}
          spaceBetween={50}
          slidesPerView={1}
          navigation
          pagination={{ clickable: true }}
          autoplay={{ delay: 4000 }}
        >
          <SwiperSlide>
            <div className="rounded-lg bg-gradient-to-r from-secondary to-primary text-white flex flex-col items-center justify-center p-10">
              <h2 className="text-5xl font-bold text-white">
                Atualize para Premium!
              </h2>
              <p className="mt-4 text-lg">
                Desfrute de recursos exclusivos com nosso plano Premium.
              </p>
              <button
                onClick={() => handleCheckout(products[0]?.prices[0]?.id)}
                className="mt-6 bg-white text-blue-700 font-bold py-3 px-8 rounded-full transition duration-300 hover:bg-gray-100"
              >
                Assinar Agora
              </button>
            </div>
          </SwiperSlide>

          <SwiperSlide>
            <div className="rounded-lg bg-gradient-to-r from-secondary to-primary text-white flex flex-col items-center justify-center p-10">
              <h2 className="text-4xl font-bold text-white">Desconto de 20%</h2>
              <p className="mt-4 text-lg">
                Atualize para o plano Premium e aproveite 20% de desconto por
                tempo limitado!
              </p>
              <button
                onClick={() => handleCheckout(products[1]?.prices[0]?.id)}
                className="mt-6 bg-white text-green-700 font-bold py-3 px-8 rounded-full transition duration-300 hover:bg-gray-100"
              >
                Ver Detalhes
              </button>
            </div>
          </SwiperSlide>

          <SwiperSlide>
            <div className="rounded-lg bg-gradient-to-r from-secondary to-primary text-white flex flex-col items-center justify-center p-10">
              <h2 className="text-5xl font-bold text-white">
                Acesso Exclusivo!
              </h2>
              <p className="mt-4 text-lg">
                Obtenha acesso a funcionalidades avançadas com nosso plano
                Empresarial.
              </p>
              <button
                onClick={() => handleCheckout(products[2]?.prices[0]?.id)}
                className="mt-6 bg-white text-orange-700 font-bold py-3 px-8 rounded-full transition duration-300 hover:bg-gray-100"
              >
                Assinar Empresarial
              </button>
            </div>
          </SwiperSlide>

          <SwiperSlide>
            <div className="rounded-lg bg-gradient-to-r from-secondary to-primary text-white flex flex-col items-center justify-center p-10">
              <h2 className="text-4xl font-bold text-white">
                Novo Serviço: Telemedicina
              </h2>
              <p className="mt-4 text-lg">
                Agora com suporte a Telemedicina! Atualize para a versão mais
                completa e conecte-se com seus pacientes de forma mais ágil.
              </p>
              <button
                onClick={() => handleCheckout(products[3]?.prices[0]?.id)}
                className="mt-6 bg-white text-red-700 font-bold py-3 px-8 rounded-full transition duration-300 hover:bg-gray-100"
              >
                Contratar Agora
              </button>
            </div>
          </SwiperSlide>
        </Swiper>

        {/* Cards dos Produtos e Serviços */}
        <section
          aria-label="Planos e Serviços disponíveis"
          className="grid gap-8 lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1 mt-8"
        >
          {products.map((product) => (
            <div
              key={product.id}
              className="bg-surface shadow-lg rounded-lg p-6 flex flex-col justify-between transform transition-transform duration-300 hover:scale-105 hover:shadow-xl"
              role="region"
              aria-labelledby={`plan-${product.id}`}
            >
              <h2
                id={`plan-${product.id}`}
                className="font-semibold text-primary mb-4 text-white-700"
              >
                {product.name}
              </h2>
              <p className="text-textSecondary mb-4">{product.description}</p>
              <button
                onClick={() => handleCheckout(product.prices[0].id)}
                className="w-full bg-primary text-white font-semibold py-3 px-4 rounded-full hover:bg-primary-hover transition-colors duration-200 mt-auto"
              >
                <FaCheckCircle className="inline-block mr-2" />
                {product.prices[0].recurring ? 'Assinar' : 'Comprar'}{' '}
                {product.name}
              </button>
            </div>
          ))}
        </section>
      </div>
    </Layout>
  );
};

export default UpgradePage;
